import {Color, Graphic} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ProcedurePageContext} from 'procedures/ProcedurePageContext';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {assertNever} from 'util/Types';

function getEmptyStepTextForProcedureType(procedureType: ProcedureType): string {
  switch (procedureType) {
    case ProcedureType.RECIPE:
      return `Tell your team how to perform this ${getProcedureUIString(
        ProceduresStringKeys.METHOD,
        procedureType,
        {stringCase: StringCase.LOWER}
      )}`;
    case ProcedureType.ASSAY:
      return `Tell your team how to run this ${getProcedureUIString(
        ProceduresStringKeys.PROCEDURE,
        procedureType,
        {
          stringCase: StringCase.LOWER,
        }
      )}`;
    default:
      assert(false, `Unexpected procedure type: ${assertNever(procedureType)}`);
  }
}

function getInstructionTextForProcedureType(procedureType: ProcedureType): string {
  switch (procedureType) {
    case ProcedureType.RECIPE:
      return `Write instructions for preparing and executing this ${getProcedureUIString(
        ProceduresStringKeys.METHOD,
        procedureType,
        {
          stringCase: StringCase.LOWER,
        }
      )}.`;
    case ProcedureType.ASSAY:
      return `Write instructions for preparing and running this ${getProcedureUIString(
        ProceduresStringKeys.PROCEDURE,
        procedureType,
        {
          stringCase: StringCase.LOWER,
        }
      )}.`;
    default:
      assert(false, `Unexpected procedure type: ${assertNever(procedureType)}`);
  }
}

export interface EmptyStepConfigSectionProps {
  onClickAddStep: () => void;
  allowAddStep: boolean;
}

export default function EmptyStepConfigSection(props: EmptyStepConfigSectionProps): JSX.Element {
  const {procedureMetadata} = ProcedurePageContext.useContext();
  const procedureType = procedureMetadata.procedureType;
  const titleText = getEmptyStepTextForProcedureType(procedureType);
  const instructionsText = getInstructionTextForProcedureType(procedureType);
  return (
    <div
      className={css(styles.centeredHorizontalSection)}
      {...getTestProps({component: 'EmptyStepConfigSection', element: 'root'})}
    >
      <div className={css(styles.centeredVerticalSection)}>
        <div className={css(styles.boldText)}>{titleText}</div>
        <div className={css(styles.middleSection)}>
          <div className={css(styles.graphicWithCaption)}>
            <Graphic name="document" className={css(styles.graphic)} />
            <div className={css(styles.graphicCaption)}>{instructionsText}</div>
          </div>
          <div className={css(styles.graphicWithCaption)}>
            <Graphic name="table" className={css(styles.graphic)} />
            <div className={css(styles.graphicCaption)}>
              Insert tables for your team to capture data against your definitions.
            </div>
          </div>
        </div>
        {props.allowAddStep && (
          <Button
            buttonStyle={ButtonStyle.PRIMARY}
            text="Add new step"
            icon="compose"
            onClick={props.onClickAddStep}
            size={ButtonSize.MEDIUM}
          />
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  boldText: {
    fontWeight: 'bold',
  },
  centeredHorizontalSection: {
    display: 'flex',
    justifyContent: 'center',
    height: '100%',
    background: Color.GRAY_50,
  },
  centeredVerticalSection: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '14px',
  },
  graphic: {
    height: '150px',
    width: '150px',
    marginBottom: '20px',
    color: Color.GRAY_400,
  },
  graphicCaption: {
    color: Color.GRAY_700,
  },
  graphicWithCaption: {
    width: '250px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    paddingLeft: '10px',
    paddingRight: '10px',
    textAlign: 'center',
  },
  middleSection: {
    display: 'flex',
    paddingTop: '30px',
    paddingBottom: '30px',
  },
});
